import { useState } from 'react';
import { UploadOnAddEvent, UploadOnRemoveEvent, UploadFileInfo } from '@progress/kendo-react-upload';
import { useNotificationContext, useUploadTranslations, useValidateTranslationCsvFile } from '@cs-cms/state-management';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const MAX_FILE_SIZE_MB = 10;

interface StaticTranslationItem {
  technicalName: string;
  reference: string;
  translation: string;
}

interface UseTranslationCsvUploadProps {
  activeLocale: any;
  translate: (key: string) => string;
}

export const useTranslationCsvUpload = ({ activeLocale, translate }: UseTranslationCsvUploadProps) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadFileInfo[]>([]);
  const [changedTranslations, setChangedTranslations] = useState<StaticTranslationItem[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const { showToast } = useNotificationContext();

  const uploadTranslationsMutation = useUploadTranslations();
  const validateCsvMutation = useValidateTranslationCsvFile();

  const validateCsvFile = (file: UploadFileInfo): string | null => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      return translate('custom.settings.adminCenter.translationManagement.upload.errors.invalidFormat');
    }

    if (file.size != null && file.size > MAX_FILE_SIZE_BYTES) {
      return translate('custom.settings.adminCenter.translationManagement.upload.errors.fileSizeExceeded').replace(
        '{maxSize}',
        MAX_FILE_SIZE_MB.toString()
      );
    }

    return null;
  };

  const onAddHandler = async (event: UploadOnAddEvent): Promise<void> => {
    if (!activeLocale) {
      return;
    }

    const newFile = event.newState[event.newState.length - 1];
    const rawFile = newFile?.getRawFile?.();
    if (!newFile || !rawFile) {
      return;
    }

    setIsProcessing(true);
    setValidationError(null);

    const validationErr = validateCsvFile(newFile);
    if (validationErr) {
      setValidationError(validationErr);
      setIsProcessing(false);
      return;
    }

    try {
      const validationResult = await validateCsvMutation.mutateAsync({
        locale: activeLocale.name,
        file: rawFile
      });

      const changes: StaticTranslationItem[] = Object.entries(validationResult.changed).map(([key, change]) => ({
        technicalName: key,
        reference: change.oldValue,
        translation: change.newValue
      }));

      setUploadedFiles([newFile]);
      setChangedTranslations(changes);
      setHasChanges(changes.length > 0);

      if (changes.length === 0) {
        showToast({
          message: translate('custom.settings.adminCenter.translationManagement.changes.noChanges'),
          type: 'info'
        });
      }
    } catch (error: any) {
      let errorMessage = translate('custom.settings.adminCenter.translationManagement.upload.errors.processingFailed');

      if (error?.message) {
        errorMessage = error.message;
      }

      setValidationError(errorMessage);
      setUploadedFiles([]);
    } finally {
      setIsProcessing(false);
    }
  };

  const onRemoveHandler = (event: UploadOnRemoveEvent): void => {
    setUploadedFiles(event.newState);
    setChangedTranslations([]);
    setHasChanges(false);
    setValidationError(null);
  };

  const removeFile = (fileToRemove: UploadFileInfo): void => {
    setUploadedFiles(prev => prev.filter(file => file !== fileToRemove));
    setChangedTranslations([]);
    setHasChanges(false);
    setValidationError(null);
  };

  const clearUpload = () => {
    setUploadedFiles([]);
    setChangedTranslations([]);
    setHasChanges(false);
    setValidationError(null);
  };

  const handleSave = async () => {
    if (!activeLocale || uploadedFiles.length === 0) {
      return;
    }

    const rawFile = uploadedFiles[0].getRawFile?.();
    if (!rawFile) {
      return;
    }

    try {
      const result = await uploadTranslationsMutation.mutateAsync({
        locale: activeLocale.name,
        file: rawFile
      });

      if (result) {
        showToast({
          message: translate('custom.settings.adminCenter.translationManagement.saveSuccess'),
          type: 'success'
        });

        clearUpload();
      }
    } catch (error: any) {
      showToast({
        message: error?.message || translate('custom.settings.adminCenter.translationManagement.saveError'),
        type: 'error'
      });
    }
  };

  return {
    uploadedFiles,
    changedTranslations,
    hasChanges,
    isProcessing,
    validationError,
    uploadTranslationsMutation,
    onAddHandler,
    onRemoveHandler,
    removeFile,
    clearUpload,
    handleSave
  };
};
